import React from 'react';
import { OfferLookupEntityOption } from '../Utils/offerLookupAddNew';
import { offersApi } from './offersApi';
import {
  extractCreatedLookupId,
  mapCountryToOfferLookupOption,
  mapHotelToOfferLookupOption,
  mapRegionToOfferLookupOption,
  OfferCountryLookupSource,
  OfferHotelLookupSource,
  OfferRegionLookupSource,
} from './offersLocationLookupMapper';

export interface CreateOfferLookupCountryRequest {
  name: string;
  description: string;
  published: boolean;
}

export interface CreateOfferLookupRegionRequest {
  name: string;
  description: string;
  countryId: number;
}

export interface CreateOfferLookupHotelRequest {
  name: string;
  shortDescription: string;
  description: string;
  location: string;
  amenities: string;
  accommodation: string;
  meals: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  published: boolean;
  displayOrder: number;
  street: string;
  city: string;
  postalCode: string;
}

interface CreateOfferLookupResult {
  id: number;
}

const offersLocationLookupApi = offersApi.injectEndpoints({
  endpoints: (builder) => ({
    getOfferLookupCountriesRaw: builder.query<OfferCountryLookupSource[], void>({
      query: () => '/countries',
    }),
    getOfferLookupRegionsRaw: builder.query<OfferRegionLookupSource[], void>({
      query: () => '/regions',
    }),
    getOfferLookupHotelsRaw: builder.query<OfferHotelLookupSource[], void>({
      query: () => '/hotels',
    }),
    createOfferLookupCountryRaw: builder.mutation<unknown, CreateOfferLookupCountryRequest>({
      query: (data) => ({
        url: '/countries',
        method: 'POST',
        body: data,
      }),
    }),
    createOfferLookupRegionRaw: builder.mutation<unknown, CreateOfferLookupRegionRequest>({
      query: (data) => ({
        url: '/regions',
        method: 'POST',
        body: data,
      }),
    }),
    createOfferLookupHotelRaw: builder.mutation<unknown, CreateOfferLookupHotelRequest>({
      query: (data) => ({
        url: '/hotels',
        method: 'POST',
        body: data,
      }),
    }),
  }),
  overrideExisting: false,
});

const {
  useGetOfferLookupCountriesRawQuery,
  useGetOfferLookupRegionsRawQuery,
  useGetOfferLookupHotelsRawQuery,
  useCreateOfferLookupCountryRawMutation,
  useCreateOfferLookupRegionRawMutation,
  useCreateOfferLookupHotelRawMutation,
} = offersLocationLookupApi;

export const useGetOfferLookupCountriesQuery = () => {
  const queryResult = useGetOfferLookupCountriesRawQuery();
  const data = React.useMemo(
    () =>
      (queryResult.data ?? [])
        .map((country) => mapCountryToOfferLookupOption(country))
        .filter((option): option is OfferLookupEntityOption => Boolean(option)),
    [queryResult.data]
  );

  return {
    ...queryResult,
    data,
  };
};

export const useGetOfferLookupRegionsQuery = () => {
  const queryResult = useGetOfferLookupRegionsRawQuery();
  const data = React.useMemo(
    () =>
      (queryResult.data ?? [])
        .map((region) => mapRegionToOfferLookupOption(region))
        .filter((option): option is OfferLookupEntityOption => Boolean(option)),
    [queryResult.data]
  );

  return {
    ...queryResult,
    data,
  };
};

export const useGetOfferLookupHotelsQuery = () => {
  const queryResult = useGetOfferLookupHotelsRawQuery();
  const data = React.useMemo(
    () =>
      (queryResult.data ?? [])
        .map((hotel) => mapHotelToOfferLookupOption(hotel))
        .filter((option): option is OfferLookupEntityOption => Boolean(option)),
    [queryResult.data]
  );

  return {
    ...queryResult,
    data,
  };
};

type CreateCountryMutationState = ReturnType<typeof useCreateOfferLookupCountryRawMutation>[1];
type CreateRegionMutationState = ReturnType<typeof useCreateOfferLookupRegionRawMutation>[1];
type CreateHotelMutationState = ReturnType<typeof useCreateOfferLookupHotelRawMutation>[1];

export const useCreateOfferLookupCountryMutation = (): [
  (payload: CreateOfferLookupCountryRequest) => Promise<CreateOfferLookupResult>,
  CreateCountryMutationState,
] => {
  const [mutate, state] = useCreateOfferLookupCountryRawMutation();

  const create = React.useCallback(
    async (payload: CreateOfferLookupCountryRequest): Promise<CreateOfferLookupResult> => {
      const response = await mutate(payload).unwrap();
      const id = extractCreatedLookupId(response, ['countryId', 'id']);
      if (!id) {
        throw new Error('Nie udało się odczytać identyfikatora nowego kraju.');
      }

      return { id };
    },
    [mutate]
  );

  return [create, state];
};

export const useCreateOfferLookupRegionMutation = (): [
  (payload: CreateOfferLookupRegionRequest) => Promise<CreateOfferLookupResult>,
  CreateRegionMutationState,
] => {
  const [mutate, state] = useCreateOfferLookupRegionRawMutation();

  const create = React.useCallback(
    async (payload: CreateOfferLookupRegionRequest): Promise<CreateOfferLookupResult> => {
      const response = await mutate(payload).unwrap();
      const id = extractCreatedLookupId(response, ['regionId', 'RegionId', 'id']);
      if (!id) {
        throw new Error('Nie udało się odczytać identyfikatora nowego regionu.');
      }

      return { id };
    },
    [mutate]
  );

  return [create, state];
};

export const useCreateOfferLookupHotelMutation = (): [
  (payload: CreateOfferLookupHotelRequest) => Promise<CreateOfferLookupResult>,
  CreateHotelMutationState,
] => {
  const [mutate, state] = useCreateOfferLookupHotelRawMutation();

  const create = React.useCallback(
    async (payload: CreateOfferLookupHotelRequest): Promise<CreateOfferLookupResult> => {
      const response = await mutate(payload).unwrap();
      const id = extractCreatedLookupId(response, [['hotelId', 'id'], 'hotelId', ['HotelId', 'id'], 'id']);
      if (!id) {
        throw new Error('Nie udało się odczytać identyfikatora nowego hotelu.');
      }

      return { id };
    },
    [mutate]
  );

  return [create, state];
};
